import './ActivityFeed.css';
import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import type { Activity, Agent } from '../types';
import { 
  PlusCircle, 
  MessageSquare, 
  FileText, 
  AlertCircle, 
  CheckCircle2, 
  ArrowRight,
  User
} from 'lucide-react';

interface ActivityFeedProps {
  activities: Activity[];
  agents: Agent[];
}

const activityIcons: Record<Activity['type'], React.ReactNode> = {
  task_created: <PlusCircle size={16} className="icon-blue" />,
  task_moved: <ArrowRight size={16} className="icon-purple" />,
  message_sent: <MessageSquare size={16} className="icon-green" />,
  document_created: <FileText size={16} className="icon-orange" />,
  agent_status_changed: <User size={16} className="icon-cyan" />,
  mention: <AlertCircle size={16} className="icon-red" />,
};

const ActivityFeed: React.FC<ActivityFeedProps> = ({ activities, agents }) => {
  const getAgentName = (id: string) => agents.find(a => a.id === id)?.name || 'Unknown';
  const getAgentEmoji = (id: string) => agents.find(a => a.id === id)?.emoji || '🤖';

  return (
    <div className="activity-feed">
      <div className="feed-header">
        <h2>Activity Feed</h2>
        <span className="live-indicator">
          <span className="live-dot"></span>
          Live
        </span>
      </div>

      <div className="feed-content">
        {activities.length === 0 ? (
          <div className="empty-state">
            <CheckCircle2 size={48} className="empty-icon" />
            <p>No recent activity</p>
            <span>Activities will appear here as work progresses</span>
          </div>
        ) : (
          <div className="activity-list">
            {activities.map((activity, index) => (
              <div 
                key={activity.id} 
                className={`activity-item ${index === 0 ? 'latest' : ''}`}
              >
                <div className="activity-icon">
                  {activityIcons[activity.type]}
                </div>
                
                <div className="activity-content">
                  <div className="activity-header">
                    <span className="activity-agent">
                      {getAgentEmoji(activity.agentId)} {getAgentName(activity.agentId)}
                    </span>
                    <span className="activity-type">{activity.type.replace('_', ' ')}</span>
                  </div>
                  
                  <p className="activity-message">{activity.message}</p>
                  
                  <span className="activity-time">
                    {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;
